import { CollapsibleFilters } from '../common/CollapsibleFilters';
import { ContentBriefFields, initialBrief } from "./ContentBriefFields";
import type { GenerationBrief } from "../../hooks/useArticleGeneration";
import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  Input,
  IconButton,
  List,
  ListItem,
  Chip,
  Alert,
  Modal,
  ModalDialog,
  ModalClose,
  Divider,
  Textarea,
  CircularProgress,
  Checkbox,
} from "@mui/joy";
import {
  PencilSimple as Edit,
  Trash as Delete,
  CheckCircle,
  Sparkle as AutoAwesome,
  MagnifyingGlass as Search,
  Tag,
} from "@phosphor-icons/react";
import { useBlogIdeas } from "../../hooks/useBlogIdeas";
import { useArticleGeneration } from "../../hooks/useArticleGeneration";
import { AreaSelectionModal } from "./AreaSelectionModal";

interface BlogIdeaListProps {
  topicalMapId: number;
  onClose?: () => void;
}

interface BlogIdea {
  id: number;
  title: string;
  description: string;
  primary_keyword: string;
  secondary_keywords: string[];
  used: boolean;
  created_at: string;
}

export const BlogIdeaList: React.FC<BlogIdeaListProps> = ({
  topicalMapId,
  onClose,
}) => {
  const [bulkBrief, setBulkBrief] = useState(initialBrief);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ title: "", description: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showAreaModal, setShowAreaModal] = useState(false);
  const [selectedBlogIdea, setSelectedBlogIdea] = useState<BlogIdea | null>(
    null,
  );

  const { blogIdeas, isLoading, updateBlogIdea, deleteBlogIdea, markAsUsed } =
    useBlogIdeas(topicalMapId);

  const { generateArticle, generateBulkArticles, isGenerating } =
    useArticleGeneration();

  const handleEdit = (idea: BlogIdea) => {
    setEditingId(idea.id);
    setEditForm({ title: idea.title, description: idea.description });
  };

  const handleSaveEdit = async () => {
    if (editingId) {
      await updateBlogIdea(editingId, editForm);
      setEditingId(null);
      setEditForm({ title: "", description: "" });
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({ title: "", description: "" });
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Are you sure you want to delete this blog idea?")) {
      await deleteBlogIdea(id);
    }
  };

  const handleGenerateSingle = (idea: BlogIdea) => {
    setSelectedBlogIdea(idea);
    setShowAreaModal(true);
  };

  const handleAreaSelection = async (
    areaId: number | null,
    brief: GenerationBrief,
  ) => {
    if (selectedBlogIdea) {
      await generateArticle(selectedBlogIdea.id, areaId, brief);
      setShowAreaModal(false);
      setSelectedBlogIdea(null);
    }
  };

  const handleGenerateBulk = async () => {
    if (selectedIds.length > 0) {
      try {
        await generateBulkArticles(topicalMapId, selectedIds, bulkBrief);
      } catch {
        return;
      }
      setSelectedIds([]);
      setShowGenerateModal(false);
    }
  };

  const handleSelectAll = () => {
    const unusedIds =
      blogIdeas
        ?.filter((idea: BlogIdea) => !idea.used)
        .map((idea: BlogIdea) => idea.id) || [];
    setSelectedIds(unusedIds);
  };

  const handleSelectNone = () => {
    setSelectedIds([]);
  };

  const toggleSelection = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const filteredIdeas = blogIdeas?.filter(
    (idea: BlogIdea) =>
      idea.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      idea.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      idea.primary_keyword.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const unusedCount =
    blogIdeas?.filter((idea: BlogIdea) => !idea.used).length || 0;

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Button
            variant="solid"
            color="primary"
            startDecorator={<AutoAwesome />}
            onClick={() => setShowGenerateModal(true)}
            disabled={selectedIds.length === 0}
          >
            Generate Articles ({selectedIds.length})
          </Button>
<CollapsibleFilters><Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Input
            placeholder="Search blog ideas..."
            startDecorator={<Search />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1 }}
          />

        </Stack></CollapsibleFilters>

        <Stack direction="row" spacing={2} alignItems="center">
          <Typography level="body-sm">
            {blogIdeas?.length || 0} total ideas • {unusedCount} available
          </Typography>
          <Button size="sm" variant="plain" onClick={handleSelectAll}>
            Select All Unused
          </Button>
          <Button size="sm" variant="plain" onClick={handleSelectNone}>
            Clear Selection
          </Button>
        </Stack>
      </Box>

      {filteredIdeas?.length === 0 ? (
        <Alert color="neutral">
          <Typography>No blog ideas found matching your search.</Typography>
        </Alert>
      ) : (
        <List sx={{ gap: 1 }}>
          {filteredIdeas?.map((idea: BlogIdea) => (
            <ListItem key={idea.id}>
              <Card variant="outlined" sx={{ width: "100%" }}>
                <CardContent>
                  {editingId === idea.id ? (
                    <Stack spacing={2}>
                      <Input
                        value={editForm.title}
                        onChange={(e) =>
                          setEditForm({ ...editForm, title: e.target.value })
                        }
                        placeholder="Blog title"
                      />
                      <Textarea
                        value={editForm.description}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            description: e.target.value,
                          })
                        }
                        placeholder="Description"
                        minRows={2}
                      />
                      <Stack direction="row" spacing={1}>
                        <Button size="sm" onClick={handleSaveEdit}>
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="plain"
                          onClick={handleCancelEdit}
                        >
                          Cancel
                        </Button>
                      </Stack>
                    </Stack>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "start", gap: 2 }}>
                      <Checkbox
                        checked={selectedIds.includes(idea.id)}
                        onChange={() => toggleSelection(idea.id)}
                        disabled={idea.used}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography level="h4" sx={{ mb: 1 }}>
                          {idea.title}
                        </Typography>
                        <Typography level="body-sm" sx={{ mb: 2 }}>
                          {idea.description}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Chip
                            variant="soft"
                            color="primary"
                            size="sm"
                            startDecorator={<Tag />}
                          >
                            {idea.primary_keyword}
                          </Chip>
                          {idea.secondary_keywords?.map((kw, idx) => (
                            <Chip
                              key={idx}
                              variant="soft"
                              color="neutral"
                              size="sm"
                            >
                              {kw}
                            </Chip>
                          ))}
                          {idea.used && (
                            <Chip
                              variant="soft"
                              color="success"
                              size="sm"
                              startDecorator={<CheckCircle />}
                            >
                              Used
                            </Chip>
                          )}
                        </Stack>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        {!idea.used && (
                          <>
                            <IconButton
                              size="sm"
                              variant="plain"
                              onClick={() => handleEdit(idea)}
                            >
                              <Edit />
                            </IconButton>
                            <Button
                              size="sm"
                              variant="solid"
                              color="primary"
                              onClick={() => handleGenerateSingle(idea)}
                              loading={isGenerating}
                            >
                              Generate Article
                            </Button>
                          </>
                        )}
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="danger"
                          onClick={() => handleDelete(idea.id)}
                        >
                          <Delete />
                        </IconButton>
                      </Stack>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </ListItem>
          ))}
        </List>
      )}

      {/* Bulk Generate Modal */}
      <Modal
        open={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
      >
        <ModalDialog sx={{ maxWidth: 500 }}>
          <ModalClose />
          <ContentBriefFields value={bulkBrief} onChange={setBulkBrief} />
          <Typography level="h3" sx={{ mb: 2 }}>
            Generate Multiple Articles
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Typography level="body-md" sx={{ mb: 3 }}>
            You've selected {selectedIds.length} blog ideas. This will generate
            articles for all selected ideas in the background. You can monitor
            progress in the Progress Tracker tab.
          </Typography>
          <Alert color="warning" sx={{ mb: 3 }}>
            <Typography level="body-sm">
              <strong>Note:</strong> Article generation uses AI tokens. Each
              article typically costs $0.02-0.05 to generate. Bulk generation
              will process up to 5 articles in parallel.
            </Typography>
          </Alert>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="plain" onClick={() => setShowGenerateModal(false)}>
              Cancel
            </Button>
            <Button
              variant="solid"
              color="primary"
              startDecorator={<AutoAwesome />}
              onClick={handleGenerateBulk}
              loading={isGenerating}
            >
              Generate {selectedIds.length} Articles
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* Area Selection Modal */}
      {showAreaModal && selectedBlogIdea && (
        <AreaSelectionModal
          open={showAreaModal}
          onClose={() => {
            setShowAreaModal(false);
            setSelectedBlogIdea(null);
          }}
          onSelect={handleAreaSelection}
          blogIdeaTitle={selectedBlogIdea.title}
        />
      )}
    </Box>
  );
};
